/**
 * Folds the event stream into turns.
 *
 * Kept as a pure function, separate from the hook and the components, because it is the
 * only genuinely stateful logic in the frontend and it is worth being able to test it with
 * a list of events and no React at all.
 */
import type { ChatEvent, ChatTurn, ToolActivity } from '@/types/events'

export function createTurn(turnId: string, question: string): ChatTurn {
  return { turnId, question, activities: [], answer: '', status: 'streaming' }
}

/**
 * Rebuild a finished turn from what the server stored.
 *
 * The transcript keeps a compact summary of each tool call — name, risk, status, latency —
 * not its arguments or results: the full record of what ran is in `tool_executions`, and
 * duplicating payloads into a chat transcript would make it a second data-retention problem.
 * So a restored card shows what happened and links onward rather than repeating everything.
 */
export function restoreTurn(stored: {
  turn_id: string
  question: string
  answer?: string | null
  status: string
  tool_calls: Array<{
    tool: string
    risk?: string | null
    status: string
    latency_ms?: number | null
    error_type?: string | null
  }>
  total_latency_ms?: number | null
  error_message?: string | null
}): ChatTurn {
  return {
    turnId: stored.turn_id,
    question: stored.question,
    answer: stored.answer ?? '',
    // The reason travels with the stored turn. Without it a reload showed every failure as
    // "This turn did not finish", hiding the one sentence that said what to do about it.
    errorMessage: stored.error_message ?? undefined,
    // A turn read back from storage is never still streaming, whatever it said when it was
    // written — the process that was streaming it is gone.
    status: stored.status === 'failed' ? 'failed' : 'complete',
    totalLatencyMs: stored.total_latency_ms ?? undefined,
    activities: stored.tool_calls.map((call, index) => ({
      // The stored summary has no call id; the position in the turn is stable and unique
      // enough to key a list that will never be patched again.
      callId: `${stored.turn_id}-${index}`,
      tool: call.tool,
      risk: (call.risk as ToolActivity['risk']) ?? 'READ',
      status: call.status === 'failed' ? 'failed' : 'success',
      arguments: {},
      latencyMs: call.latency_ms ?? undefined,
      errorType: call.error_type ?? undefined,
      startedAt: 0,
    })),
  }
}

function patchActivity(
  turn: ChatTurn,
  callId: string,
  patch: (activity: ToolActivity) => ToolActivity,
): ChatTurn {
  let found = false
  const activities = turn.activities.map((activity) => {
    if (activity.callId !== callId) return activity
    found = true
    return patch(activity)
  })
  // An update for a call we never saw selected means events arrived out of order.
  // Dropping it is better than inventing an activity with no tool name.
  return found ? { ...turn, activities } : turn
}

export function applyEvent(current: ChatTurn, event: ChatEvent): ChatTurn {
  // A pause lasts until anything else happens: the next round starting, a tool, text, or
  // the end of the turn. Clearing it here rather than per case means no event can leave a
  // countdown running on a turn that has moved on.
  const turn: ChatTurn =
    current.waiting && event.type !== 'model_waiting' ? { ...current, waiting: undefined } : current

  switch (event.type) {
    case 'turn_started':
      return { ...turn, status: 'streaming' }

    case 'thinking':
      return turn

    case 'tool_selected': {
      // Keyed by call_id, so a redelivered event replaces rather than duplicates.
      if (turn.activities.some((activity) => activity.callId === event.call_id)) return turn
      const activity: ToolActivity = {
        callId: event.call_id,
        tool: event.tool,
        risk: event.risk,
        status: 'pending',
        arguments: event.arguments,
        startedAt: Date.now(),
      }
      return { ...turn, activities: [...turn.activities, activity] }
    }

    case 'tool_started':
      return patchActivity(turn, event.call_id, (activity) => ({
        ...activity,
        status: 'running',
        startedAt: Date.now(),
      }))

    case 'tool_completed':
      return patchActivity(turn, event.call_id, (activity) => ({
        ...activity,
        status: 'success',
        latencyMs: event.latency_ms,
        resultSummary: event.result_summary,
        requestId: event.request_id,
      }))

    case 'tool_failed':
      return patchActivity(turn, event.call_id, (activity) => ({
        ...activity,
        // A policy denial is not a failure of the tool: the safety layer worked. It gets
        // its own status so the UI can say so instead of showing a red error.
        status: event.error_type === 'POLICY_VIOLATION' ? 'denied' : 'failed',
        latencyMs: event.latency_ms,
        errorType: event.error_type,
        errorMessage: event.message,
        requestId: event.request_id,
      }))

    case 'assistant_delta':
      return { ...turn, answer: turn.answer + event.text }

    case 'model_waiting':
      return {
        ...turn,
        waiting: { reason: event.reason, until: Date.now() + event.seconds * 1000 },
      }

    case 'turn_finished':
      return { ...turn, status: 'complete', totalLatencyMs: event.total_latency_ms }

    case 'turn_failed':
      // The type travels with the message: running out of credits mid-turn is not a fault
      // and must not be drawn as one, and only the view can tell the difference.
      return {
        ...turn,
        status: 'failed',
        errorType: event.error_type,
        errorMessage: event.message,
      }

    default: {
      // Exhaustiveness: a new backend event type fails the build here.
      const unreachable: never = event
      return unreachable
    }
  }
}

/** Total time the model spent waiting on tools, for the turn footer. */
export function toolTimeMs(turn: ChatTurn): number {
  return turn.activities.reduce((total, activity) => total + (activity.latencyMs ?? 0), 0)
}

